import React, { Fragment } from 'react'
import { v4 as uuid } from 'uuid'
import { Draggable } from 'react-beautiful-dnd'
import classNames from 'classnames'
import { toString, isEmpty } from 'lodash'
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { DragAndDropButton } from '@/components/dragDrop/DragAndDropButton'
import { CopyButton } from '@/components/buttons/CopyButton'
import NoData from '@/components/NoData'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { shouldShowCopyButton } from './helpers'

import { AdvancedTableCell } from './AdvancedTableCell'

export const AdvancedTableBody = ({
  filteredRows,
  columns,
  actions,
  index,
  borderlessRows,
  isBulkSelectable,
  selectedRows,
  handleBulkSelectSingle,
  reorderable,
  stickyAction,
  wrapColumnContent,
  dropProvided,
  searchFilters,
  dataGrid,
  disabledSelection,
  expandable,
  handleExpand,
  expandedRows,
}) => {
  const colSpan =
    columns.length + (actions ? 1 : 0) + (reorderable ? 1 : 0) + (isBulkSelectable ? 1 : 0)
  const isNotFiltering =
    isEmpty(searchFilters) || Object.values(searchFilters).every((v) => isEmpty(v))

  const renderRow = (row) => {
    return columns.map((column) => {
      const sharedRowClasses = {
        'no-wrap': wrapColumnContent || column.noWrap,
        'sticky-column': column.sticky,
        'sticky-column-selectable-offset': isBulkSelectable && dataGrid && column.sticky,
        'borderless-row': borderlessRows,
        'full-width-row': column.fullWidthRow,
        [`${column.headerAlignment}-aligned`]: Boolean(column.headerAlignment),
        collapsing: column.collapsing,
      }

      if (row[column.accessor]?.as) {
        return (
          <td
            key={column.accessor}
            data-testid={`custom-component-${column.accessor}`}
            data-column={column.accessor}
            {...row[column.accessor].cellProps}
            className={classNames(row[column.accessor].cellProps?.className, sharedRowClasses)}
          >
            {row[column.accessor].as}
          </td>
        )
      }
      return (
        <td
          key={column.accessor}
          data-testid="table-cell"
          data-column={column.accessor}
          className={classNames(sharedRowClasses)}
        >
          {shouldShowCopyButton(column, row[column.accessor]) ? (
            <div className="flex-align-center medium-gap">
              <AdvancedTableCell row={row} column={column} />
              <CopyButton
                content={
                  column.formatCopyValue
                    ? column.formatCopyValue(row[column.accessor])
                    : row[column.accessor]
                }
                iconOnly
              />
            </div>
          ) : (
            <AdvancedTableCell row={row} column={column} />
          )}
        </td>
      )
    })
  }

  return (
    <tbody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
      {isEmpty(filteredRows) ? (
        <tr className="no-hover">
          <td colSpan={colSpan}>
            <div className="empty-table">
              <NoData />
            </div>
          </td>
        </tr>
      ) : (
        filteredRows.map((row, rowIndex) => {
          const isSelected = isBulkSelectable && selectedRows?.includes(row[index])
          const dragIndex = toString(row[index]) || uuid()

          const expandableRow =
            expandable &&
            (row.expandedContent || !isEmpty(row.expandedRows) || !isEmpty(row.childTableData))

          return (
            <Draggable
              draggableId={dragIndex}
              index={rowIndex}
              key={dragIndex}
              isDragDisabled={!reorderable || !isNotFiltering}
            >
              {(dragProvided, dragSnapshot) => {
                return (
                  <Fragment key={index === 'uuid' ? uuid() : `${row[index]}`}>
                    <tr
                      data-testid={row[index]}
                      ref={dragProvided.innerRef}
                      {...row.rowProps}
                      {...dragProvided.draggableProps}
                      className={classNames({
                        'row-is-dragging': dragSnapshot.isDragging,
                        'row-is-selected': isSelected,
                        'ai-agent-row': row.is_ai_call || row.isAiAgent,
                      })}
                    >
                      {/* This cell does not stop propagation, so a table that is both
                          bulk-selectable and supplies `rowProps.onClick` would fire the row
                          handler and the select together on one click. No such table exists yet. */}
                      {isBulkSelectable && (
                        <td
                          className={classNames('collapsing', {
                            'borderless-row': borderlessRows,
                            'sticky-column': dataGrid,
                            'bulk-selectable-column': dataGrid,
                          })}
                        >
                          <div className="flex-align-center">
                            <Checkbox
                              data-testid={`bulk-select-${row[index]}`}
                              // The row id, not the first cell's value — for custom cells that
                              // value is a `{ as, value }` object.
                              aria-label={`Select row ${row[index]}`}
                              checked={isSelected}
                              onChange={() => {
                                if (handleBulkSelectSingle) {
                                  handleBulkSelectSingle(row[index], row)
                                }
                              }}
                              disabled={disabledSelection}
                            />
                          </div>
                        </td>
                      )}
                      {reorderable && (
                        <td
                          className={classNames('collapsing', { 'borderless-row': borderlessRows })}
                        >
                          <div className="flex-align-center">
                            <InteractiveTooltip
                              placement="top-start"
                              hide={isNotFiltering}
                              content="Cannot reorder while searching or filtering playlists"
                            >
                              <DragAndDropButton
                                snapshot={dragSnapshot}
                                provided={dragProvided}
                                disabled={!isNotFiltering}
                              />
                            </InteractiveTooltip>
                          </div>
                        </td>
                      )}
                      {expandable && (
                        <td
                          className={classNames('collapsing', {
                            'borderless-row': borderlessRows,
                            'sticky-column': dataGrid,
                          })}
                        >
                          <div className="flex-align-center">
                            <Button
                              basic
                              small
                              square
                              disabled={!expandableRow}
                              data-testid={`expand-${row[index]}`}
                              onClick={() => handleExpand(row[index])}
                            >
                              {!expandableRow ? null : expandedRows.includes(row[index]) ? (
                                <IconChevronDown />
                              ) : (
                                <IconChevronRight />
                              )}
                            </Button>
                          </div>
                        </td>
                      )}
                      {renderRow(row)}
                      {!isEmpty(actions) && (
                        <td
                          className={classNames('action-cell', { 'sticky-action': stickyAction })}
                        >
                          <div className="actions-icons">
                            {actions.map((action) => {
                              if (action.trigger) {
                                return <Fragment key={action.label}>{action.trigger(row)}</Fragment>
                              }

                              return (
                                <Button
                                  key={action.label}
                                  basic
                                  small
                                  square={!!action.icon}
                                  tooltip={action.label}
                                  data-testid={`action-button-${action.label}`}
                                  onClick={(event) => action.fn(event, row)}
                                >
                                  {action.icon || action.label}
                                </Button>
                              )
                            })}
                          </div>
                        </td>
                      )}
                    </tr>
                    {expandedRows.includes(row[index]) && !isEmpty(row.childTableData) && (
                      <tr>
                        <td colSpan={columns.length + (actions ? 1 : 0)}>
                          <AdvancedTable
                            rows={row.childTableData}
                            columns={row.childColumns}
                            pagination={false}
                            wrapColumnContent={false}
                          />
                        </td>
                      </tr>
                    )}

                    {expandedRows.includes(row[index]) &&
                      row.expandedContent &&
                      isEmpty(row.childTableData) && (
                        <tr>
                          <td colSpan={columns.length + 1}>{row.expandedContent}</td>
                        </tr>
                      )}

                    {expandedRows.includes(row[index]) &&
                      isEmpty(row.childTableData) &&
                      !isEmpty(row.expandedRows) &&
                      row.expandedRows.map((expandedRow) => (
                        <tr>
                          <td />
                          {renderRow(expandedRow)}
                        </tr>
                      ))}
                  </Fragment>
                )
              }}
            </Draggable>
          )
        })
      )}
      {dropProvided.placeholder}
    </tbody>
  )
}
